import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface NotificationMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  duration?: number; // Auto-hide duration in milliseconds (0 = no auto-hide)
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<NotificationMessage[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private notificationId = 0;

  constructor() {}

  /**
   * Show a success notification
   */
  showSuccess(message: string, duration: number = 5000): string {
    return this.showNotification(message, 'success', duration);
  }

  /**
   * Show an error notification
   */
  showError(message: string, duration: number = 7000): string {
    return this.showNotification(message, 'error', duration);
  }

  /**
   * Show an info notification
   */
  showInfo(message: string, duration: number = 5000): string {
    return this.showNotification(message, 'info', duration);
  }

  /**
   * Show a warning notification
   */
  showWarning(message: string, duration: number = 6000): string {
    return this.showNotification(message, 'warning', duration);
  }

  /**
   * Show a notification with custom type
   */
  showNotification(message: string, type: 'success' | 'error' | 'info' | 'warning', duration: number = 5000): string {
    const id = `notification_${++this.notificationId}_${Date.now()}`;
    const notification: NotificationMessage = {
      id,
      message,
      type,
      duration
    };

    const currentNotifications = this.notificationsSubject.value;
    this.notificationsSubject.next([...currentNotifications, notification]);

    // Auto-hide notification if duration is specified
    if (duration > 0) {
      setTimeout(() => {
        this.hideNotification(id);
      }, duration);
    }

    return id;
  }

  /**
   * Hide a specific notification
   */
  hideNotification(id: string): void {
    const currentNotifications = this.notificationsSubject.value;
    const filteredNotifications = currentNotifications.filter(notification => notification.id !== id);
    this.notificationsSubject.next(filteredNotifications);
  }

  /**
   * Hide all notifications
   */
  hideAllNotifications(): void {
    this.notificationsSubject.next([]);
  }

  /**
   * Get current notifications
   */
  getCurrentNotifications(): NotificationMessage[] {
    return this.notificationsSubject.value;
  }

  /**
   * Show notification from API error response
   */
  showApiError(error: any): string {
    let message = 'An error occurred. Please try again.';
    
    if (error.error && error.error.errors) {
      const errorMessages: string[] = [];
      Object.values(error.error.errors).forEach((val: any) => {
        if (Array.isArray(val)) {
          errorMessages.push(...val);
        } else {
          errorMessages.push(val);
        }
      });
      message = errorMessages.join(', ');
    } else if (error.error && error.error.message) {
      message = error.error.message;
    } else if (error.message) {
      message = error.message;
    }

    return this.showError(message);
  }

  /**
   * Show notification from API success response
   */
  showApiSuccess(response: any): string {
    const message = response.message || 'Operation completed successfully';
    return this.showSuccess(message);
  }
}
